import { useState } from "react";
import { toast } from "sonner";
import { RotateCcw, Loader2, Zap } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

// Student re-enrol from a cancelled summary, via the normal credit / payment flow.
export function ReEnrolButton({ rebook, onDone }) {
  const [busy, setBusy] = useState(false);
  if (!rebook || !rebook.in_future || !rebook.bookable || rebook.already_enrolled) return null;
  if (rebook.removed_by_coach) {
    return <div className="text-sm text-[#A3A3A3]" data-testid="reenrol-removed">Your coach removed you from this session. Contact them if you'd like to be added back.</div>;
  }
  if (rebook.spaces_left <= 0) {
    return <div className="text-sm text-[#FF9F1C]" data-testid="reenrol-full">This session is now full, so you can't re-enrol.</div>;
  }
  const id = rebook.offering_id;
  const done = () => { toast.success("You're back in"); onDone?.(id); };
  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && typeof d === "object" ? d.message : formatApiError(d)) || "Couldn't re-enrol");
    } finally { setBusy(false); }
  };
  const useCredit = () => run(async () => { await api.post(`/offerings/${id}/use-credit`); done(); });
  const pay = () => run(async () => {
    if (rebook.price > 0) {
      const { data } = await api.post("/checkout/offering", { offering_id: id, origin_url: window.location.origin });
      if (data.url) { window.location.href = data.url; return; }
    }
    await api.post(`/offerings/${id}/enrol`);
    done();
  });
  const credit = rebook.accept_credits && rebook.credits > 0;
  const btn = "min-h-[44px] px-5 py-2.5 text-xs uppercase tracking-wider font-bold bg-[#CCFF00] text-black inline-flex items-center gap-2 active:scale-95 transition-transform disabled:opacity-50";
  return (
    <div className="flex flex-col items-start gap-2" data-testid="reenrol-block">
      {credit ? (
        <button type="button" onClick={useCredit} disabled={busy} className={btn} data-testid="reenrol-credit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} Re-enrol · use 1 credit
        </button>
      ) : (
        <button type="button" onClick={pay} disabled={busy} className={btn} data-testid="reenrol-button">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
          {rebook.price > 0 ? `Re-enrol · pay £${rebook.price}` : "Re-enrol"}
        </button>
      )}
      {credit && rebook.price > 0 && rebook.allow_pay_when_credit_available && (
        <button type="button" onClick={pay} disabled={busy} data-testid="reenrol-pay-instead"
          className="text-[10px] uppercase tracking-wider text-[#A3A3A3] hover:text-white underline underline-offset-2">
          Pay £{rebook.price} instead →
        </button>
      )}
      <div className="text-xs text-[#737373]">{rebook.spaces_left} space{rebook.spaces_left === 1 ? "" : "s"} left</div>
    </div>
  );
}
